import React, { useEffect, useMemo, useState, useContext, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FeatureType } from "../../constants/FeatureToggles";
import {
  useMaterialReactTable,
  MaterialReactTable,
} from "material-react-table";
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBCard,
  MDBCardBody,
  MDBCardTitle,
  MDBInput,
} from "mdb-react-ui-kit";
import { Box, Tooltip, IconButton } from "@mui/material";
import {
  Edit as EditIcon,
  Delete,
  InfoOutlined,
} from "@mui/icons-material";
import AbsModal from "../../common/absModal/AbsModal";
import { ClaimsContext } from "../../services/ClaimsService";
import "./Help.scss";
import BreadCrumb from "../../common/breadCrumb/BreadCrumb";
import { HELP_DATA } from "./constants";
import { Button, COLORS, Icon, ICONS, Modal } from "abs-ui-components";
import axiosHttp from "../../services/interceptor";
import { emitToasterMsg } from "../../services/toasterUtility";
import { useSelector } from "react-redux";
import { isErSuperAdmin } from "../../utils/roleUtils";

const PRIMARY_BUTTON_COLOR = '#002A4E';

const Contacts = () => {

  const navigate = useNavigate();
  const user = useSelector((state) => state.userInfo.userData);
  const isSuperAdmin = useMemo(() => isErSuperAdmin(user), [user]);

  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState([]);
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formOffice, setFormOffice] = useState("");
  const [formName, setFormName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [saving, setSaving] = useState(false);

  const [deleteContactOpen, setDeleteContactOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState({ id: null, name: "" });
  const [modalDeleting, setModalDeleting] = useState(false);

  /* --------------------------- Feature Toggle -------------------------- */
      const location = useLocation();
      const wcnNumber = location.state?.wcnNumber;
  
      const { userHasFeature, userFeaturesAreLoaded, fetchUserFeatures, checkWcnDelta } =
      useContext(ClaimsContext);
  
      useEffect(() => {
          if (!userFeaturesAreLoaded || checkWcnDelta(wcnNumber)) {
            fetchUserFeatures(wcnNumber);
          }
        }, [userFeaturesAreLoaded, fetchUserFeatures, checkWcnDelta, wcnNumber]);
  
      const canShowContacts = useMemo(() => {
          return userHasFeature(FeatureType.ViewSupportContact);
      }, [userHasFeature]);
      const canCreateContacts = useMemo(() => {
          return isSuperAdmin && userHasFeature(FeatureType.CreateSupportContact);
      }, [isSuperAdmin, userHasFeature]);
      const canEditContacts = useMemo(() => {
          return isSuperAdmin && userHasFeature(FeatureType.EditSupportContact);
      }, [isSuperAdmin, userHasFeature]);
      const canDeleteContacts = useMemo(() => {
          return isSuperAdmin && userHasFeature(FeatureType.DeleteSupportContact);
      }, [isSuperAdmin, userHasFeature]);

  /* --------------------------- Derived Data -------------------------- */
  const breadcrumb = useMemo(
    () => [
      { label: HELP_DATA["nav-support-text"], url: "/support", navId: "support_navId" },
      { label: HELP_DATA["nav-help-text"], url: "/support", navId: "help_navId" },
      { label: "Contacts", url: "", navId: "contacts_navId" },
    ],
    []
  );

  const tableData = useMemo(() => {
    return contacts;
  }, [contacts]);

  const onEdit = useCallback((item) => {
    setEditing(item);
    setFormOffice(item.office || "");
    setFormName(item.name || "");
    setFormEmail(item.email || "");
    setModalOpen(true);
  }, []);

  const onDelete = useCallback((id) => {
    const item = contacts.find((c) => c.id === id);
    setContactToDelete({ id, name: item?.name || "" });
    setDeleteContactOpen(true);
  }, [contacts]);

  const columns = useMemo(() => {
    const baseColumns = [
      { header: "Office Location", accessorKey: "office" },
      { header: "Contact Name", accessorKey: "name" },
      {
        header: "Contact Information (email)",
        accessorKey: "email",
        Cell: ({ cell }) => {
          const email = cell.getValue();
          const mailto = `mailto:${encodeURIComponent(email)}`;
          return (
            <a href={mailto} onClick={(e) => e.stopPropagation()}>
              {email}
            </a>
          );
        },
      },
    ];

    if (canEditContacts || canDeleteContacts) {
      baseColumns.push({
        header: "Actions",
        id: "actions",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const item = row.original;
          const actionIconColor = "#6c757d";
          return (
            <Box sx={{ display: "flex", gap: "0.5rem" }}>
              {canEditContacts && (
                <Tooltip title="Edit">
                  <IconButton
                    size="small"
                    onClick={() => onEdit(item)}
                    sx={{ color: actionIconColor }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {canDeleteContacts && (
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onDelete(item.id)}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        },
      });
    }

    return baseColumns;
  }, [canEditContacts, canDeleteContacts, onEdit, onDelete]);

  /* --------------------------- API Calls & Callbacks -------------------------- */
  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);

      const requestBody = {
        globalFilter: search || "",
        filters: [],
        pagination: { page: 1, pageSize: 50 },
        sortOption: {
          SortField: sorting[0]?.id ?? "",
          IsAscending: sorting[0] ? !sorting[0].desc : false,
        },
      };

      const response = await axiosHttp.post("api/Support/GetContactList", requestBody);

      const payload = Array.isArray(response?.data?.Data)
        ? response.data.Data
        : (Array.isArray(response?.data) ? response.data : []);

      const mapped = payload.map((r) => ({
        id: r.ContactId,
        office: r.OfficeLocation,
        name: r.ContactName,
        email: r.Email,
      }));

      setContacts(mapped);
    } catch (err) {
      console.error("Failed to load contacts", err);
      setContacts([]);
      emitToasterMsg("error", "Failed to load contacts.");
    } finally {
      setLoading(false);
    }
  }, [search, sorting]);

  const confirmDeleteContact = async () => {
    if (!contactToDelete?.id) return;
    const contactId = contactToDelete.id;

    try {
      setModalDeleting(true);

      const response = await axiosHttp.delete("api/Support/DeleteContact", {
        params: { contactId },
      });

      const isSuccess = response?.data?.isSuccess === true || response?.status === 200;
      if (isSuccess) {
        setContacts((prev) => prev.filter((c) => c.id !== contactId));
        emitToasterMsg("success", "Contact deleted successfully.");
      } else {
        emitToasterMsg("error", "Failed to delete contact.");
      }
    } catch (err) {
      console.error("Failed to delete contact", err);
      emitToasterMsg("error", "Failed to delete contact.");
    } finally {
      setModalDeleting(false);
      setDeleteContactOpen(false);
      setContactToDelete({ id: null, name: "" });
    }
  };

  const onSave = async () => {
    const office = formOffice.trim();
    const name = formName.trim();
    const email = formEmail.trim();

    if (!office || !name || !isValidEmail(email)) return;

    if (editing) {
      try {
        setSaving(true);

        const body = {
          OfficeLocation: office,
          ContactName: name,
          Email: email,
        };

        await axiosHttp.post("api/Support/EditContact", body, {
          params: { contactId: editing.id },
        });

        emitToasterMsg("success", "Contact updated successfully.");
        setModalOpen(false);
        resetForm();
        await fetchContacts();
      } catch (err) {
        console.error("Failed to update contact", err);
        emitToasterMsg("error", "Failed to update contact.");
      } finally {
        setSaving(false);
      }
      return;
    }

    try {
      setSaving(true);
      const body = {
        OfficeLocation: office,
        ContactName: name,
        Email: email,
      };

      const response = await axiosHttp.post("api/Support/CreateContact", body);
      const isSuccess = response?.status === 200 || response?.data?.isSuccess === true;

      if (isSuccess) {
        emitToasterMsg("success", "Contact created successfully.");
        setModalOpen(false);
        resetForm();
        await fetchContacts();
      } else {
        emitToasterMsg("error", "Failed to create contact.");
      }
    } catch (err) {
      console.error("Failed to create contact", err);
      emitToasterMsg("error", "Failed to create contact.");
    } finally {
      setSaving(false);
    }
  };

  /* --------------------------- Effects -------------------------- */
  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  // debounced fetch on search changes
  useEffect(() => {
    const t = setTimeout(() => fetchContacts(), 300);
    return () => clearTimeout(t);
  }, [fetchContacts]);

  /* --------------------------- Table Configuration -------------------------- */
  const table = useMaterialReactTable({
    columns,
    data: tableData,
    enableRowSelection: false,
    enableStickyHeader: true,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableGlobalFilter: true,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    manualFiltering: true,
    manualSorting: true,
    onSortingChange: setSorting,
    onGlobalFilterChange: setSearch,
    initialState: {
      density: "compact",
      showGlobalFilter: true,
      columnOrder:
        (canEditContacts || canDeleteContacts)
          ? ["office", "name", "email", "actions"]
          : ["office", "name", "email"],
    },
    muiTableContainerProps: { sx: { maxHeight: "calc(100vh - 376px)" } },
    muiTableHeadCellProps: ({ column }) => ({
      id: `${column.columnDef.accessorKey || "actions"}`,
      sx: {
        color: "rgb(255 255 255)",
        "& .MuiButtonBase-root": {
          color: "white !important",
          "& .MuiTableSortLabel-icon": { color: "white !important" },
        },
      },
    }),
    muiTableHeadRowProps: {
      sx: {
        backgroundColor: "#042a4c !important",
        "& .MuiTableCell-head::before": { backgroundColor: "#042a4c !important" },
      },
    },
    muiTablePaperProps: {
      sx: { boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)" },
    },
    state: {
      isLoading: loading,
      globalFilter: search,
      sorting,
    },
  });

  /* --------------------------- Handlers & Utilities -------------------------- */
  const resetForm = () => {
    setEditing(null);
    setFormOffice("");
    setFormName("");
    setFormEmail("");
  };

  const openAdd = () => {
    resetForm();
    setModalOpen(true);
  };

  const cancelDeleteContact = () => {
    setDeleteContactOpen(false);
    setContactToDelete({ id: null, name: "" });
  };

  const isValidEmail = (email) => {
    const trimmed = email.trim();
    const domainRegex = /^[A-Za-z0-9._%+-]+@(abswavesight\.com|notes\.eagle\.org|abs-group\.com|ex\.eagle\.org|eagle\.mail\.onmicrosoft\.com|eagle\.onmicrosoft\.com|absdigitalsolutions\.com|absconsulting\.com|eagle\.org|abs-qe\.com|abs-ns\.com)$/i;
    return domainRegex.test(trimmed);
  };

  const allowedDomainsHint =
    "Allowed domains: eagle.org, abswavesight.com, notes.eagle.org, abs-group.com, ex.eagle.org, eagle.mail.onmicrosoft.com, eagle.onmicrosoft.com, absdigitalsolutions.com, absconsulting.com, abs-qe.com, abs-ns.com";

  /* --------------------------- JSX -------------------------- */
  if (!canShowContacts) {
    return (
      <MDBContainer fluid className="px-2 px-md-3 py-4">
        <div className="mb-3">
          <BreadCrumb
            items={breadcrumb}
            onCrumbClick={(item) => {
              if (item.url) navigate(item.url);
            }}
          />
        </div>
        <MDBRow className="gx-2">
          <MDBCol md="12" lg="12">
            <MDBCard className="h-100">
              <MDBCardBody>
                <MDBCardTitle className="mb-0">Contacts</MDBCardTitle>
                <div className="mt-3">You don’t have permission to view contacts.</div>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
        </MDBRow>
      </MDBContainer>
    );
  }

  return (
    <MDBContainer fluid className="px-2 px-md-3 py-4">
      {/* Breadcrumb navigation */}
      <div className="mb-3">
        <BreadCrumb
          items={breadcrumb}
          onCrumbClick={(item) => {
            if (item.url) navigate(item.url);
          }}
        />
      </div>

      {/* Page content: contacts card */}
      <MDBRow className="gx-2">
        <MDBCol md="12" lg="12">
          <MDBCard className="h-100">
            <MDBCardBody>
              {/* Card header: title + actions */}
              <div className="d-flex align-items-center justify-content-between">
                <MDBCardTitle className="mb-0">Contacts</MDBCardTitle>
                {(canCreateContacts || canEditContacts) && (
                  <Button
                    onClick={openAdd}
                    startIcon={<Icon icon={ICONS.ADD} />}
                    primaryBackgroundColor={PRIMARY_BUTTON_COLOR}
                    primaryColor={PRIMARY_BUTTON_COLOR}
                  >
                    ADD CONTACT
                  </Button>
                )}
              </div>

              {/* Table section */}
              <div className="list-height">
                <MaterialReactTable table={table} />
              </div>
            </MDBCardBody>
          </MDBCard>
        </MDBCol>
      </MDBRow>

      {/* Add/Edit Contact Modal */}
      {(canCreateContacts || canEditContacts) && (
        <AbsModal
          isOpen={modalOpen}
          title={editing ? "Edit contact" : "Add contact"}
          onClose={setModalOpen}
          close={() => {
            setModalOpen(false);
            resetForm();
          }}
        >
          {/* Form fields */}
          <div className="mb-3">
            <MDBInput
              label="Office Location"
              value={formOffice}
              onChange={(e) => setFormOffice(e.target.value)}
              disabled={saving}
            />
          </div>

          <div className="mb-3">
            <MDBInput
              label="Contact Name"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              disabled={saving}
            />
          </div>

          <div className="mb-3">
            <MDBInput
              label={
                <span className="d-inline-flex align-items-center gap-1">
                  Email
                  <Tooltip title={allowedDomainsHint} placement="right">
                    <IconButton
                      size="small"
                      aria-label="Allowed email domains"
                      onMouseDown={(e) => e.preventDefault()}
                      sx={{ color: "#002a4e", p: 0 }}
                    >
                      <InfoOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </span>
              }
              type="email"
              value={formEmail}
              onChange={(e) => setFormEmail(e.target.value)}
              disabled={saving}
            />
            {!isValidEmail(formEmail) && (
              <div style={{ color: "red", fontSize: "0.875rem", marginTop: "0.25rem" }}>
                Email domain must be a valid ABS domain.
              </div>
            )}
          </div>

          {/* Modal actions: Add then Cancel (match app styling) */}
          <div className="help-modal-footer">
            <Button
              className="save-button"
              onClick={onSave}
              disabled={saving || !formOffice.trim() || !formName.trim() || !isValidEmail(formEmail)}
              primaryBackgroundColor={PRIMARY_BUTTON_COLOR}
              primaryColor={PRIMARY_BUTTON_COLOR}
            >
              <span className="d-inline-flex align-items-center">
                {/* Save button spinner */}
                {saving && (
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  />
                )}
                {editing ? "Save" : "Add"}
              </span>
            </Button>
            <Button
              className="clear-button"
              onClick={() => {
                setModalOpen(false);
                resetForm();
              }}
              disabled={saving}
            >
              Cancel
            </Button>
          </div>
        </AbsModal>
      )}

      {/* Delete confirmation modal */}
      {canDeleteContacts && (
        <div className="confirmation-modal-container">
          <Modal
            isOpen={deleteContactOpen}
            modalHeader
            padding={"70px"}
            headerHeight={"50px"}
            showFooter
            onCloseClickListener={cancelDeleteContact}
            footer={
              // Modal footer actions: delete/cancel
              <div className="d-flex justify-content-end fixed-action-btns p-4">
                <Button
                  className="modal-delete-btn"
                  onClick={confirmDeleteContact}
                  primaryColor={COLORS.RED.DARK}
                  primaryBackgroundColor={COLORS.RED.LIGHT}
                  disabled={modalDeleting}
                >
                  {/* Delete button spinner */}
                  {modalDeleting ? (
                    <span className="d-inline-flex align-items-center">
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Deleting...
                    </span>
                  ) : (
                    "Delete"
                  )}
                </Button>
                <Button className="modal-cancel-btn" onClick={cancelDeleteContact} disabled={modalDeleting}>
                  Cancel
                </Button>
              </div>
            }
          >
            {/* Modal body text */}
            <div>
              Are you sure you want to delete this contact{contactToDelete?.name ? `: "${contactToDelete.name}"` : ""}?
            </div>
          </Modal>
        </div>
      )}
    </MDBContainer>
  );
};

export default Contacts;